import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

/**
 * Destructive confirmation dialog for the platform admin's bulk-delete
 * actions on tenants + users.
 *
 * Props:
 *  - open, onOpenChange: standard controlled Radix
 *  - kind: "tenant" | "user" — controls copy
 *  - count: how many rows the caller is about to purge
 *  - onConfirm(): async — parent runs the API call; we handle the busy
 *    state + auto-close on success
 */
export default function BulkDeleteDialog({
  open,
  onOpenChange,
  kind,
  count,
  onConfirm,
}) {
  const [busy, setBusy] = useState(false);

  // Reset busy when the dialog is dismissed so a subsequent open starts fresh.
  useEffect(() => {
    if (!open) setBusy(false);
  }, [open]);

  const label = kind === "tenant" ? "tenant" : "kullanıcı";
  const title =
    kind === "tenant"
      ? `${count} tenant'ı kalıcı olarak sil?`
      : `${count} kullanıcıyı kalıcı olarak sil?`;
  const detail =
    kind === "tenant"
      ? "Tenant'a ait tüm kullanıcılar, projeler, görevler, mesajlar, dosyalar, davetler ve diğer veriler geri getirilemez şekilde silinecek."
      : "Kullanıcının oluşturduğu görevler, yorumlar, mesajlar ve tüm içerik geri getirilemez şekilde silinecek. Başka kullanıcılar tarafından oluşturulmuş ve yalnızca bu kullanıcıya atanmış görevler atamasız hale gelecek.";

  const handleConfirm = async () => {
    try {
      setBusy(true);
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Parent surfaces the toast; we just reset busy so user can retry.
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <AlertDialogContent data-testid="bulk-delete-dialog">
        <AlertDialogHeader>
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <AlertDialogTitle data-testid="bulk-delete-title">
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <span className="block">{detail}</span>
            <span className="block font-medium text-red-700">
              Bu işlem geri alınamaz.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={busy}
            data-testid="bulk-delete-cancel"
          >
            Vazgeç
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="bulk-delete-confirm"
            onClick={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            disabled={busy}
            className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Siliniyor…
              </>
            ) : (
              `Evet, ${count} ${label} sil`
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
